import { useForm } from '@tanstack/react-form';
import {
  maxValue,
  minValue,
  nonEmpty,
  object,
  pipe,
  string,
  toNumber,
} from 'valibot';
import { useEffect, useMemo } from 'react';
import { selectedDeviceStateAtom } from '../../../models/atoms/device_atoms';
import { useRecoilValue } from 'recoil';
import { MHMDistanceInputs } from '../MHM/MHMDistanceInputs';
import { useWarningLevel } from '../../../hooks/useWarningLevel';
import { useTranslation } from '../../../contexts/TranslationContext';

type Props = {
  canEditSession: boolean;
  defaultWarningPercentage: number;
  onChange(
    percentage: number,
    errorMessage: [string, Record<string, string>],
  ): void;
};

export function DeviceWarningLevelMM({
  canEditSession,
  defaultWarningPercentage,
  onChange,
}: Props) {
  const { distance } = useTranslation();
  const selectedDevice = useRecoilValue(selectedDeviceStateAtom);
  const { minMM, maxMM, toWarningDistance, toWarningPercentage } =
    useWarningLevel();

  const mmSchema = useMemo(
    () =>
      pipe(
        string(),
        nonEmpty('Please provide a valid number!'),
        toNumber('Please provide a valid number!'),
        minValue(minMM, 'Not in the allowable range millimeters'),
        maxValue(maxMM, 'Not in the allowable range millimeters'),
      ),
    [minMM, maxMM],
  );

  const form = useForm({
    defaultValues: {
      warningMM: String(toWarningDistance(defaultWarningPercentage)),
    },
    validators: { onChange: object({ warningMM: mmSchema }) },
    listeners: {
      onChangeDebounceMs: 1,
      onChange({ fieldApi }) {
        const percentage = toWarningPercentage(Number(fieldApi.state.value));
        const errorMessage =
          fieldApi.state.meta.errorMap.onChange?.[0].message ?? '';
        onChange(percentage, [
          errorMessage,
          {
            minMillimeters: distance.show(minMM),
            maxMillimeters: distance.show(maxMM),
          },
        ]);
      },
    },
  });

  useEffect(() => {
    form.reset();
  }, [selectedDevice]);

  return (
    <form.Field name="warningMM">
      {field => {
        return (
          <MHMDistanceInputs.Millimetres
            allowDecimal
            value={field.state.value}
            onChange={e => field.handleChange(e.target.value)}
            disabled={!canEditSession}
            error={!field.state.meta.isValid}
            slotProps={{ htmlInput: { sx: { minWidth: '3.5rem' } } }}
            sx={{ width: 'min-content' }}
            autoComplete="off"
          />
        );
      }}
    </form.Field>
  );
}
